import { AccommodationIcon } from '@/assets/icons'
import { countLabel } from '@/utils/plural'
import { getInitials } from '@/utils/userNames'
import PageLoader from '@components/PageLoader'
import { useBreadcrumbs } from '@context/BreadcrumbContext'
import { useAuth } from '@context/AuthContext'
import { useTrip, useTripMembers } from '@hooks'
import { useAccommodations } from '@hooks/useAccommodations'
import { useCreateAccommodation } from '@hooks/useCreateAccommodation'
import { useUploadFiles } from '@hooks/useUploadFiles'
import { formatUtcInstant, parseUtcInstant } from '@lib/datetime'
import { GetUserType } from '@orbit/api-types'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import PageHeading from '@components/PageHeading'
import AddAccommodationModal, { AddAccommodationSubmitData } from './AddAccommodationModal'
import {
    AccommodationCard,
    AccommodationGrid,
    AccommodationName,
    AccommodationsPageContainer,
    AddButton,
    Avatar,
    AvatarStack,
    ContentContainer,
    DateBlock,
    DateLabel,
    DateRow,
    DateValue,
    EmptyState,
    EmptyStateButton,
    EmptyStateContainer,
    EmptyStateIcon,
    EmptyStateText,
    ErrorContainer,
    GuestCount,
    GuestRow,
    HeaderSection,
    LoadingContainer,
    OverflowAvatar,
    YoureAGuestPill,
} from './AccommodationsPage.styles'

const MAX_VISIBLE_AVATARS = 4

const AccommodationsPage: React.FC = () => {
    const { tripId } = useParams<{ tripId: string }>()
    const navigate = useNavigate()
    const { user } = useAuth()
    const { data: trip, isLoading: tripLoading, error: tripError } = useTrip(tripId)
    const { data: accommodationsData, isLoading: accommodationsLoading, error: accommodationsError } = useAccommodations(tripId)
    const { data: membersData } = useTripMembers(tripId)

    const createAccommodation = useCreateAccommodation()
    const uploadFiles = useUploadFiles()

    const [isAddModalOpen, setIsAddModalOpen] = useState(false)

    const members = membersData?.members ?? []
    const accommodations = accommodationsData?.accommodations ?? []

    useBreadcrumbs(
        trip
            ? [
                { label: trip.name, path: `/trips/${tripId}` },
                { label: 'Accommodations' },
            ]
            : []
    )

    const getMemberById = (userId: number): GetUserType | undefined => {
        return members.find(m => m.id === userId)
    }


    const sortedAccommodations = [...accommodations].sort((a, b) => {
        const dateA = parseUtcInstant(a.checkInDate)
        const dateB = parseUtcInstant(b.checkInDate)
        return dateA.getTime() - dateB.getTime()
    })

    const handleAddAccommodation = async (data: AddAccommodationSubmitData) => {
        if (!tripId) return
        const { pendingFiles, ...accommodationData } = data
        try {
            const created = await createAccommodation.mutateAsync({ tripId, accommodationData })
            if (pendingFiles && pendingFiles.length > 0) {
                try {
                    await uploadFiles.mutateAsync({
                        tripId,
                        files: pendingFiles,
                        resourceType: 'ACCOMMODATION',
                        resourceId: created.id,
                    })
                } catch {
                    toast.error('Accommodation added, but attachments failed to upload.')
                }
            }
            toast.success('Accommodation added!')
            setIsAddModalOpen(false)
        } catch {
            toast.error('Failed to add accommodation. Please try again.')
        }
    }

    if (tripLoading || accommodationsLoading) {
        return (
            <LoadingContainer>
                <PageLoader label="Loading accommodations..." />
            </LoadingContainer>
        )
    }

    if (tripError || accommodationsError) {
        return (
            <ErrorContainer>
                Error loading accommodations: {tripError?.message || accommodationsError?.message}
            </ErrorContainer>
        )
    }

    if (!trip) {
        return <LoadingContainer>Trip not found</LoadingContainer>
    }

    return (
        <AccommodationsPageContainer>
            {accommodations.length === 0 ? (
                <EmptyStateContainer>
                    <EmptyState>
                        <EmptyStateIcon>
                            <AccommodationIcon sx={{ fontSize: 128 }} />
                        </EmptyStateIcon>
                        <EmptyStateText>
                            No accommodations yet. Add your first accommodation to make sure everyone has a place to stay.
                        </EmptyStateText>
                        <EmptyStateButton onClick={() => setIsAddModalOpen(true)}>
                            Add Accommodation
                        </EmptyStateButton>
                    </EmptyState>
                </EmptyStateContainer>
            ) : (
                <ContentContainer>
                    <HeaderSection>
                        <PageHeading
                            title="Accommodations"
                            subtitle={`${countLabel(accommodations.length, 'place')}`}
                        />
                        <AddButton onClick={() => setIsAddModalOpen(true)}>
                            + Add Accommodation
                        </AddButton>
                    </HeaderSection>

                    <AccommodationGrid>
                        {sortedAccommodations.map(accommodation => {
                            const guests = accommodation.guests ?? []
                            const visibleGuests = guests.slice(0, MAX_VISIBLE_AVATARS)
                            const overflowCount = guests.length - MAX_VISIBLE_AVATARS
                            const isUserGuest = user ? guests.some(g => g.userId === user.id) : false

                            return (
                                <AccommodationCard
                                    key={accommodation.id}
                                    onClick={() => navigate(`/trips/${tripId}/accommodations/${accommodation.id}`)}
                                >
                                    <AccommodationName>{accommodation.name}</AccommodationName>

                                    <DateRow>
                                        <DateBlock>
                                            <DateLabel>Check-in</DateLabel>
                                            <DateValue>{formatUtcInstant(accommodation.checkInDate, 'datetime')}</DateValue>
                                        </DateBlock>
                                        <DateBlock $align="right">
                                            <DateLabel>Check-out</DateLabel>
                                            <DateValue>{formatUtcInstant(accommodation.checkOutDate, 'datetime')}</DateValue>
                                        </DateBlock>
                                    </DateRow>

                                    <GuestRow>
                                        <AvatarStack>
                                            {visibleGuests.map((guest, index) => {
                                                const member = getMemberById(guest.userId)
                                                if (!member) return null
                                                return (
                                                    <Avatar key={guest.userId} $index={index}>
                                                        {member.profilePicture ? (
                                                            <img src={member.profilePicture} alt={getInitials(member)} />
                                                        ) : (
                                                            getInitials(member)
                                                        )}
                                                    </Avatar>
                                                )
                                            })}
                                            {overflowCount > 0 && (
                                                <OverflowAvatar $index={MAX_VISIBLE_AVATARS}>
                                                    +{overflowCount}
                                                </OverflowAvatar>
                                            )}
                                        </AvatarStack>
                                        <GuestCount>
                                            {countLabel(guests.length, 'guest')}
                                        </GuestCount>
                                        {isUserGuest && (
                                            <YoureAGuestPill>You're a guest</YoureAGuestPill>
                                        )}
                                    </GuestRow>
                                </AccommodationCard>
                            )
                        })}
                    </AccommodationGrid>
                </ContentContainer>
            )}

            <AddAccommodationModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSubmit={handleAddAccommodation}
                members={members}
                isSubmitting={createAccommodation.isPending}
            />
        </AccommodationsPageContainer>
    )
}

export default AccommodationsPage
